// Acompanhamento mensal do agrupamento (#5783) — aba dentro de Agrupamentos de Faturamento.
//
// Uma linha por UC do agrupamento no mês, com o estado real e o placar do fechamento. A
// classificação vem de src/lib/conferenciaFaturamento.ts (a mesma da Conferência do Faturamento)
// via src/lib/fechamentoAgrupamento.ts — nada é reimplementado aqui.
//
// Ações por UC reaproveitam o que já existe: "Faturar" abre a fatura na etapa em que ela está,
// com os mesmos modais de Faturas e Cobranças (validar → calcular → emitir → enviar), e "Pular
// cálculo" grava com os mesmos campos da Janela de Cobrança (PularCalculoCampo +
// CAMPOS_ZERADOS_SEM_INJECAO), para não divergir do resto do sistema.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BadgeCheck, CalendarClock, CheckCircle2, Clock, Copy, ExternalLink, Eye, FileSpreadsheet, FileText, Package, Loader2, Lock, Pencil, Receipt, RefreshCw, Search, SkipForward, Unlock, XCircle } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import ConfirmModal from '../../src/components/ConfirmModal';
import TablePagination from '../../src/components/TablePagination';
import InfoTip from '../../src/components/InfoTip';
import PularCalculoCampo, { ESTADO_PULO_INICIAL, type EstadoPulo } from './components/PularCalculoCampo';
import FaturaValidationModal from './FaturaValidationModal';
import FaturaCalculationModal from './FaturaCalculationModal';
import BoletoEmissionModal from './BoletoEmissionModal';
import BoletoEmitidoModal from './BoletoEmitidoModal';
import CobrancaEnviadaModal from './CobrancaEnviadaModal';
import { etapaDaFatura, ROTULO_ETAPA_FLUXO } from '../../src/lib/etapaFluxoFatura';
import {
  competenciaParaData,
  dataParaCompetencia,
  montarFechamento,
  motivoNaoPodeFechar,
  resumirFechamento,
  ROTULO_ESTADO,
  type EstadoNoFechamento,
  type LinhaFechamento,
  type UcDoAgrupamento,
} from '../../src/lib/fechamentoAgrupamento';
import { ROTULO_DETALHE, mesDeOrdem, ordemMes } from '../../src/lib/conferenciaFaturamento';
import { montarRegua, frasePrazo, situacaoMalote, ROTULO_MALOTE, ROTULO_PRAZO, type EstadoPrazoEmissao } from '../../src/lib/reguaPrazosAgrupamento';
import { CAMPOS_ZERADOS_SEM_INJECAO } from '../../src/lib/faturaSemInjecao';
import { emitirCobrancaAvulsa } from '../../src/lib/emitirCobrancaAvulsa';
import { isContaSicoob, NOME_CONTA_SICOOB } from '../../src/lib/contaSicoob';
import { calcularValoresBoleto, montarCamposBoletoAgrupado, motivoNaoPodeEmitir } from '../../src/lib/emissaoBoletoAgrupado';
import { gerarPdfFaturaAgrupada } from '../../src/lib/faturaAgrupadaPdf';
import { motivoArquivoRecusado } from '../../src/lib/maloteAgrupamento';
import { avaliarPagamentoRecebido, ROTULO_SITUACAO_PAGAMENTO, type SituacaoPagamento } from '../../src/lib/pagamentoRetencaoAgrupamento';
import { apagarComprovanteMalote, enviarComprovanteMalote, enviarComprovanteRetencao, linkComprovanteMalote } from '../../src/lib/maloteAgrupamentoStorage';
import type { AgrupamentoFaturamento, Fatura } from '../../types';

const PAGINA_BANCO = 1000;

const CAMPOS_FATURA = [
  'id', 'uc_codigo', 'uc_codigo_norm', 'cliente_nome', 'mes_referencia', 'classificacao', 'status_fatura',
  'leitura_anterior', 'leitura_atual', 'proxima_leitura', 'data_apresentacao', 'data_vencimento', 'created_at',
  'emitido_em', 'emitido_por_nome', 'pular_calculo_em', 'pular_calculo_por_nome', 'motivo_pular_calculo',
  'motivo_pular_calculo_codigo', 'num_cobranca_boleto', 'linha_digitavel_boleto', 'url_boleto',
  'empresa_parceira_id', 'status_boleto_vigor', 'cancelado_em', 'motivo_atraso_emissao',
  'justificativa_atraso_emissao', 'energia_injetada_gd1_kwh', 'saldo_ugs_vigor', 'historico_emissoes',
  'pular_calculo_detalhe',
  // `faturas` não tem unidade_consumidora_id: a ligação com a UC é pelo número (uc_codigo /
  // uc_codigo_norm), e é assim que conferenciaFaturamento.ts casa as linhas.
  'valor_boleto_vigor', 'valor_fatura_distribuidora',
].join(', ');

const ESTILO_CAMPO =
  'rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200';

const ESTILO_ESTADO: Record<EstadoNoFechamento, { classe: string; Icone: typeof CheckCircle2 }> = {
  faturada: { classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icone: CheckCircle2 },
  cobrada_a_parte: { classe: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300', Icone: Receipt },
  pulada: { classe: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', Icone: SkipForward },
  dispensada: { classe: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300', Icone: BadgeCheck },
  aguardando_fatura: { classe: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icone: Clock },
  pendente: { classe: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', Icone: XCircle },
};

/** Cor do cartão da régua: verde no prazo, âmbar perto do limite, vermelho depois dele. */
const ESTILO_REGUA: Record<EstadoPrazoEmissao, string> = {
  no_prazo: 'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200',
  atencao: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200',
  vencido: 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200',
  emitido_no_prazo: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200',
  emitido_fora_do_prazo: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200',
};

const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const mesAtual = () => {
  const d = new Date();
  return mesDeOrdem(d.getFullYear() * 12 + d.getMonth());
};
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (v: string | null | undefined) => {
  if (!v) return '—';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

async function buscarTudo<T>(montar: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const todas: T[] = [];
  for (let de = 0; ; de += PAGINA_BANCO) {
    const { data, error } = await montar(de, de + PAGINA_BANCO - 1);
    if (error) throw error;
    todas.push(...(data ?? []));
    if (!data || data.length < PAGINA_BANCO) return todas;
  }
}

interface Props {
  agrupamento: AgrupamentoFaturamento;
  /** UCs vinculadas ao agrupamento, já carregadas pela tela de cadastro. */
  ucs: UcDoAgrupamento[];
  /** Muda quando a tela de fora lançou faturas (importação de borderô): relê o mês. */
  recarregarSinal?: number;
  /** Mês que abre selecionado ("SET/2026"), vindo do card da Secretaria no quadro de faturas. */
  mesInicial?: string | null;
}

const AcompanhamentoMensalAgrupamento: React.FC<Props> = ({ agrupamento, ucs, recarregarSinal, mesInicial }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  // Reabrir é de adm/gerente (a regra real está na RPC). Esconder o botão de quem não pode evita
  // o clique que só recebe "sem permissão". `profile` pode ser null mesmo logado: o AuthContext
  // destrava a UI por timeout, então o botão some em vez de aparecer quebrado.
  const podeReabrir = profile?.role === 'adm' || profile?.role === 'gerente';
  const [mes, setMes] = useState(() => (mesInicial && ordemMes(mesInicial) != null ? mesInicial : mesAtual()));
  const [faturas, setFaturas] = useState<any[]>([]);
  const [fechamento, setFechamento] = useState<{
    id: number; status: string; total_vigor: number | null; fechado_em: string | null; emitido_em: string | null;
    // Boleto agrupado (#5785).
    fatura_boleto_id: number | null; valor_cobrado: number | null; num_cobranca_boleto: string | null;
    linha_digitavel_boleto: string | null; url_boleto: string | null; quitado_em: string | null;
    // Malote (#5788).
    malote_entregue_em: string | null; malote_registrado_em: string | null;
    malote_observacao: string | null; malote_anexo_path: string | null;
    // Pagamento com retenção (#6003).
    valor_abatimento: number | null; valor_recebido: number | null; valor_retido: number | null;
    situacao_pagamento: SituacaoPagamento | null; pagamento_registrado_em: string | null;
    pagamento_observacao: string | null; comprovante_retencao_path: string | null;
  } | null>(null);
  const [dispensas, setDispensas] = useState<Map<number, string>>(new Map());
  /** Cadastro de feriados (#5787): `incluir` acrescenta, `excluir` suprime um padrão. */
  const [feriados, setFeriados] = useState<{ incluir: string[]; excluir: string[] }>({ incluir: [], excluir: [] });
  const [ajusteDatas, setAjusteDatas] = useState<{ limiteEmissao: string; auditoria: string; vencimento: string; malote: string; motivo: string } | null>(null);
  const [datasSalvas, setDatasSalvas] = useState<{ limiteEmissao: string | null; auditoria: string | null; vencimento: string | null; malote: string | null; motivo: string | null }>({ limiteEmissao: null, auditoria: null, vencimento: null, malote: null, motivo: null });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [soPendentes, setSoPendentes] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Fatura aberta no fluxo. É a linha COMPLETA, lida na hora do clique: a lista do mês carrega só
  // CAMPOS_FATURA, e entregar essa linha enxuta ao modal abria o cálculo zerado e com total "NaN"
  // (faltavam tarifa, descontos e parcelamento) — e confirmar ali gravaria esses zeros na fatura.
  const [faturaAberta, setFaturaAberta] = useState<Fatura | null>(null);
  const [abrindoFaturaId, setAbrindoFaturaId] = useState<number | null>(null);
  /** Entre um passo e o seguinte: esconde o modal até a fatura voltar do banco na etapa nova. */
  const [trocandoEtapa, setTrocandoEtapa] = useState(false);
  /** Fatura cuja trava de edição é desta tela. Ref porque as releituras assíncronas conferem se o fluxo ainda está aberto. */
  const fluxoRef = useRef<{ faturaId: number; userId: string } | null>(null);
  const [pulando, setPulando] = useState<LinhaFechamento | null>(null);
  // Motivo do pulo, já conferido contra a injeção da conta (PularCalculoCampo).
  const [estadoPulo, setEstadoPulo] = useState<EstadoPulo>(ESTADO_PULO_INICIAL);
  const [gravando, setGravando] = useState(false);
  // Erro específico do modal "Pular cálculo": o banner de `erro` fica atrás do overlay do
  // ConfirmModal, então uma falha na gravação nunca aparecia pra quem estava com o modal aberto.
  const [erroPular, setErroPular] = useState<string | null>(null);
  // Emissão do boleto agrupado (#5785).
  const [confirmandoEmitir, setConfirmandoEmitir] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  /** Teste (só adm): a edge preenche um boleto fictício e marca a fatura como sandbox. */
  const [emitirTeste, setEmitirTeste] = useState(false);
  // Fatura agrupada em PDF (#5786).
  const [gerandoPdf, setGerandoPdf] = useState(false);
  // A mesma fatura agrupada em Excel (pedido da Meyryene, 20/10/2026).
  const [gerandoExcel, setGerandoExcel] = useState(false);
  // Malote (#5788): registro da entrega com comprovante.
  const [registrandoMalote, setRegistrandoMalote] = useState(false);
  const [salvandoMalote, setSalvandoMalote] = useState(false);
  const [dataMalote, setDataMalote] = useState('');
  const [obsMalote, setObsMalote] = useState('');
  const [arquivoMalote, setArquivoMalote] = useState<File | null>(null);
  const [erroMalote, setErroMalote] = useState<string | null>(null);
  const [confirmandoDesfazerMalote, setConfirmandoDesfazerMalote] = useState(false);

  // Pagamento com retencao (#6003): o titulo vai cheio ao banco e a Prefeitura paga o liquido,
  // entao receber menos que o boleto e o normal. Quem registra quanto entrou e esta tela.
  const [registrandoPagamento, setRegistrandoPagamento] = useState(false);
  const [salvandoPagamento, setSalvandoPagamento] = useState(false);
  const [valorRecebido, setValorRecebido] = useState('');
  const [dataPagamento, setDataPagamento] = useState('');
  const [obsPagamento, setObsPagamento] = useState('');
  const [arquivoRetencao, setArquivoRetencao] = useState<File | null>(null);
  const [erroPagamento, setErroPagamento] = useState<string | null>(null);
  // Fechar / reabrir / dispensar (#5784).
  const [acaoFechamento, setAcaoFechamento] = useState(false);
  const [confirmandoFechar, setConfirmandoFechar] = useState(false);
  const [confirmandoPadrao, setConfirmandoPadrao] = useState(false);
  const [reabrindo, setReabrindo] = useState(false);
  const [dispensando, setDispensando] = useState<LinhaFechamento | null>(null);
  /** Motivo livre da reabertura e da dispensa (o banco exige os dois). */
  const [motivoTexto, setMotivoTexto] = useState('');
  // Erro de reabrir/dispensar mostrado dentro do próprio modal (mesmo motivo do erroPular acima):
  // o banner `erro` do topo fica atrás do overlay do ConfirmModal e passava despercebido.
  const [erroAcaoFechamento, setErroAcaoFechamento] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const competencia = competenciaParaData(mes);

      const [fs, fech] = await Promise.all([
        buscarTudo<any>((de, ate) =>
          supabase.from('faturas').select(CAMPOS_FATURA)
            .eq('mes_referencia', mes).is('excluido_em', null)
            // Sem o `or`, `neq` também descartaria as linhas com ambiente NULL.
            .or('ambiente.is.null,ambiente.neq.sandbox')
            .order('id').range(de, ate) as any),
        // O fechamento do mês nasce sozinho na primeira abertura (decisão de 15/09/2026).
        competencia
          ? supabase.rpc('abrir_fechamento_agrupamento', { p_agrupamento_id: agrupamento.id, p_competencia: competencia })
          : Promise.resolve({ data: null, error: null }),
      ]);
      setFaturas(fs);
      if ((fech as any)?.error) throw (fech as any).error;
      const linha = (fech as any)?.data;
      setFechamento(linha ? {
        id: linha.id, status: linha.status, total_vigor: linha.total_vigor, fechado_em: linha.fechado_em, emitido_em: linha.emitido_em ?? null,
        fatura_boleto_id: linha.fatura_boleto_id ?? null, valor_cobrado: linha.valor_cobrado ?? null,
        num_cobranca_boleto: linha.num_cobranca_boleto ?? null, linha_digitavel_boleto: linha.linha_digitavel_boleto ?? null,
        url_boleto: linha.url_boleto ?? null, quitado_em: linha.quitado_em ?? null,
        malote_entregue_em: linha.malote_entregue_em ?? null, malote_registrado_em: linha.malote_registrado_em ?? null,
        malote_observacao: linha.malote_observacao ?? null, malote_anexo_path: linha.malote_anexo_path ?? null,
        valor_abatimento: linha.valor_abatimento ?? null, valor_recebido: linha.valor_recebido ?? null,
        valor_retido: linha.valor_retido ?? null, situacao_pagamento: linha.situacao_pagamento ?? null,
        pagamento_registrado_em: linha.pagamento_registrado_em ?? null,
        pagamento_observacao: linha.pagamento_observacao ?? null,
        comprovante_retencao_path: linha.comprovante_retencao_path ?? null,
      } : null);
      setDatasSalvas({
        limiteEmissao: linha?.limite_emissao_ajustado ?? null,
        auditoria: linha?.auditoria_ajustada ?? null,
        vencimento: linha?.vencimento_ajustado ?? null,
        malote: linha?.malote_ajustado ?? null,
        motivo: linha?.motivo_ajuste_datas ?? null,
      });

      // Feriados cadastrados na tela (#5787). Falhar aqui não pode derrubar o mês: a régua volta
      // ao cálculo padrão, que é o comportamento anterior.
      const { data: fer, error: erroFer } = await supabase
        .from('feriados')
        .select('data, tipo')
        .gte('data', `${mes.slice(4)}-01-01`)
        .lte('data', `${Number(mes.slice(4)) + 1}-12-31`);
      if (erroFer) {
        console.error('Feriados da régua de prazos:', erroFer);
        setFeriados({ incluir: [], excluir: [] });
      } else {
        setFeriados({
          incluir: (fer ?? []).filter((f: any) => f.tipo === 'inclui').map((f: any) => String(f.data).slice(0, 10)),
          excluir: (fer ?? []).filter((f: any) => f.tipo === 'exclui').map((f: any) => String(f.data).slice(0, 10)),
        });
      }

      // Dispensas do mês (#5784): UC sem fatura liberada com motivo. Sem fechamento não há dispensa.
      if (linha?.id) {
        const { data: ds, error: erroDs } = await supabase
          .from('agrupamento_fechamento_dispensas')
          .select('unidade_consumidora_id, motivo')
          .eq('fechamento_id', linha.id);
        if (erroDs) throw erroDs;
        setDispensas(new Map((ds ?? []).map((d: any) => [d.unidade_consumidora_id as number, d.motivo as string])));
      } else {
        setDispensas(new Map());
      }
    } catch (e: any) {
      console.error('Acompanhamento mensal:', e);
      setErro(e?.message || 'Não foi possível carregar o mês.');
    } finally {
      setCarregando(false);
    }
  }, [mes, agrupamento.id]);

  useEffect(() => { carregar(); }, [carregar, recarregarSinal]);
  useEffect(() => { setPagina(0); }, [mes, busca, soPendentes, agrupamento.id]);

  const linhas = useMemo(
    () => montarFechamento({ ucs, faturas, dispensadas: dispensas.keys(), hojeIso: hojeIso() }),
    [ucs, faturas, dispensas],
  );
  const resumo = useMemo(() => resumirFechamento(linhas), [linhas]);
  const trava = motivoNaoPodeFechar(resumo);
  const estaFechado = fechamento?.status === 'fechado' || fechamento?.status === 'emitido';
  const bloqueioEmissao = motivoNaoPodeEmitir(agrupamento, fechamento);
  const valoresBoleto = calcularValoresBoleto(fechamento?.total_vigor, agrupamento.retencao_percentual);
  const competencia = competenciaParaData(mes);
  // Prévia do que o banco vai concluir (#6003), com a mesma função que a RPC repete em SQL: quem
  // digita vê na hora se o valor fecha o mês ou deixa pendência.
  const previaPagamento = useMemo(() => {
    const recebido = Number(valorRecebido.replace(/\./g, '').replace(',', '.'));
    if (!fechamento || !Number.isFinite(recebido) || recebido <= 0) return null;
    return avaliarPagamentoRecebido({
      bruto: fechamento.valor_cobrado ?? fechamento.total_vigor,
      retencao: fechamento.valor_abatimento ?? 0,
      recebido,
    });
  }, [valorRecebido, fechamento?.valor_cobrado, fechamento?.total_vigor, fechamento?.valor_abatimento]);

  // Régua do contrato (#5787): emitir até o último dia útil do mês seguinte, auditar no 1º dia
  // útil e entregar o malote até o 5º. Considera feriado; a régua individual não vale para
  // UC de agrupamento ativo.
  const regua = useMemo(
    () => (competencia
      ? montarRegua({
          competencia,
          diaVencimento: agrupamento.dia_vencimento,
          hojeIso: hojeIso(),
          emitidoEm: fechamento?.emitido_em ?? null,
          feriados,
          regra: {
            diaLimiteEmissao: agrupamento.dia_limite_emissao,
            diaAuditoriaUtil: agrupamento.dia_auditoria_util,
            diaMaloteUtil: agrupamento.dia_malote_util,
            diasPagamentoEdital: agrupamento.dias_pagamento_edital,
          },
          ajustes: {
            limiteEmissao: datasSalvas.limiteEmissao,
            auditoria: datasSalvas.auditoria,
            vencimento: datasSalvas.vencimento,
            malote: datasSalvas.malote,
          },
        })
      : null),
    [competencia, agrupamento, fechamento?.emitido_em, feriados, datasSalvas],
  );

  // Situação do malote (#5788): só existe depois da emissão, que é quando o malote sai.
  const malote = useMemo(
    () => (regua && fechamento?.status === 'emitido'
      ? situacaoMalote(regua, fechamento.malote_entregue_em, hojeIso())
      : null),
    [regua, fechamento?.status, fechamento?.malote_entregue_em],
  );

  /** Qual das 4 datas do cartão veio de ajuste manual, pelo rótulo mostrado na tela. */
  const ajustadaPorRotulo: Record<string, boolean> = {
    'Emitir até': !!regua?.ajustadas.limiteEmissao,
    'Auditoria': !!regua?.ajustadas.auditoria,
    'Vencimento': !!regua?.ajustadas.vencimento,
    'Malote e NF': !!regua?.ajustadas.malote,
  };

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const termoNum = termo.replace(/\D/g, '').replace(/^0+/, '');
    return linhas.filter(l => {
      if (soPendentes && l.resolvida) return false;
      if (!termo) return true;
      return (l.cliente ?? '').toLowerCase().includes(termo) ||
        (!!termoNum && l.numeroUc.replace(/\D/g, '').replace(/^0+/, '').includes(termoNum));
    });
  }, [linhas, busca, soPendentes]);

  const visiveis = filtradas.slice(pagina * pageSize, pagina * pageSize + pageSize);

  const opcoesMes = useMemo(() => {
    const atual = ordemMes(mesAtual())!;
    const meses = Array.from({ length: 13 }, (_, i) => mesDeOrdem(atual + 1 - i));
    // Mês aberto pelo link do quadro de faturas, se for mais antigo que a lista, entra no fim dela.
    return mesInicial && ordemMes(mesInicial) != null && !meses.includes(mesInicial) ? [...meses, mesInicial] : meses;
  }, [mesInicial]);

  /**
   * Toda gravação do fechamento passa por aqui: mostra o retorno real do banco e recarrega.
   * Devolve se deu certo — quem chama decide o que fazer com o modal (fechar só no sucesso,
   * pra não descartar o motivo digitado nem esconder o erro atrás do overlay).
   */
  const chamarRpc = async (nome: string, args: Record<string, unknown>, aoConfirmar: (r: any) => string): Promise<boolean> => {
    setAcaoFechamento(true);
    setErro(null);
    setAviso(null);
    try {
      const { data, error } = await supabase.rpc(nome, args);
      if (error) throw error;
      if ((data as any)?.ok !== true) throw new Error('O banco não confirmou a operação. Nada foi considerado gravado.');
      setAviso(aoConfirmar(data));
      await carregar();
      return true;
    } catch (e: any) {
      const mensagem = e?.code === '42501' ? (e?.message || 'Sem permissão para esta ação.') : (e?.message || 'A ação falhou.');
      setErro(mensagem);
      setErroAcaoFechamento(mensagem);
      return false;
    } finally {
      setAcaoFechamento(false);
    }
  };

  const fecharMes = () => {
    if (!competencia) return;
    chamarRpc('fechar_fechamento_agrupamento',
      { p_agrupamento_id: agrupamento.id, p_competencia: competencia, p_observacoes: null },
      r => r.ja_estava_fechado
        ? 'Este mês já estava fechado; nada foi alterado.'
        : `Mês fechado: ${r.resolvidas} de ${r.ucs} UC(s), total ${reais(Number(r.total_vigor ?? 0))}.`);
  };

  /**
   * Boleto agrupado (#5785). Três passos no banco, e cada um reconfere: preparar (valor e trava de
   * duplicidade; com R$ 0,00 já conclui sem título), ligar a fatura AVULSO ao fechamento ANTES de
   * registrar no Sicoob (se o vínculo falhar, não sobra boleto solto) e concluir só depois de o
   * título existir de verdade — o registro é conferido dentro de emitirCobrancaAvulsa.
   */
  const emitirBoleto = async () => {
    if (!fechamento) return;
    // Sem régua não há vencimento para o boleto — melhor dizer isso do que o clique não fazer nada.
    if (!regua) {
      setErro('Não foi possível calcular o vencimento deste mês. Atualize a tela e tente de novo.');
      return;
    }
    setEmitindo(true);
    setErro(null);
    setAviso(null);
    try {
      const { data: prep, error: erroPrep } = await supabase.rpc('preparar_emissao_agrupamento', { p_fechamento_id: fechamento.id });
      if (erroPrep) throw erroPrep;
      if ((prep as any)?.ok !== true) throw new Error('O banco não confirmou a preparação. Nada foi emitido.');
      if ((prep as any).sem_titulo) {
        setAviso('Total de R$ 0,00: o mês foi marcado como emitido sem boleto no banco. A relação das UCs sai mesmo assim.');
        await carregar();
        return;
      }
      // A conta é do agrupamento: o uc_codigo da fatura agrupada não é uma UC, então
      // deixar o helper deduzir cairia sempre na Associação.
      const conta = (prep as any).conta_sicoob;
      if (!isContaSicoob(conta)) throw new Error('O banco não devolveu a conta Sicoob do agrupamento. Nada foi emitido.');
      const valorCobrado = Number((prep as any).valor_cobrado);
      // Título pelo valor cheio e a retenção como abatimento (#6003): é o que deixa a Prefeitura
      // pagar o líquido sem o banco recusar no vencimento.
      const valorAbatimento = Number((prep as any).valor_abatimento ?? 0);
      const campos = montarCamposBoletoAgrupado({
        agrupamento, competencia: mes, valorCobrado, valorAbatimento, vencimento: regua.vencimento, ucsCobradas: resumo.faturadas,
      });
      await emitirCobrancaAvulsa({
        campos,
        ucCodigo: campos.uc_codigo,
        nomeQuem: profile?.full_name || user?.email || 'Usuário',
        conta,
        sandbox: emitirTeste,
        aposCriarFatura: async (faturaId) => {
          const { data, error } = await supabase.rpc('vincular_boleto_fechamento', { p_fechamento_id: fechamento.id, p_fatura_id: faturaId });
          if (error) throw error;
          if ((data as any)?.ok !== true) throw new Error('O banco não confirmou o vínculo do boleto com o mês.');
        },
      });
      const { data: fim, error: erroFim } = await supabase.rpc('concluir_emissao_agrupamento', { p_fechamento_id: fechamento.id });
      if (erroFim) throw erroFim;
      if ((fim as any)?.ok !== true) throw new Error('O boleto foi registrado, mas o mês não foi marcado como emitido. Atualize a tela antes de tentar de novo.');
      setAviso(`Boleto${emitirTeste ? ' de TESTE' : ''} emitido: ${reais(valorCobrado)}, vencimento ${dataBr(regua.vencimento)}.`);
      await carregar();
    } catch (e: any) {
      setErro(e?.code === '42501' ? (e?.message || 'Sem permissão para emitir.') : (e?.message || 'A emissão falhou.'));
      await carregar();
    } finally {
      setEmitindo(false);
    }
  };

  /**
   * Fatura agrupada (#5786): capa da cobrança, relação das UCs e um demonstrativo por unidade.
   * É desenhada na hora, a partir do fechamento — dá para gerar quantas vezes precisar.
   *
   * Com o mês ainda aberto, a mesma função devolve a PRÉVIA do malote (#6262): a situação de agora,
   * com marca d'água e sem boleto. Quem decide que é prévia é o servidor, pelo status do fechamento;
   * gerar não fecha o mês nem grava nada.
   */
  const baixarFaturaAgrupada = async () => {
    if (!fechamento || !regua) return;
    setGerandoPdf(true);
    setErro(null);
    try {
      const blob = await gerarPdfFaturaAgrupada(fechamento.id, regua.vencimento);
      const url = URL.createObjectURL(blob);
      // Bloqueador de pop-up devolve null: nesse caso o arquivo é baixado, em vez de o clique não
      // fazer nada e parecer que a geração falhou.
      const aba = window.open(url, '_blank', 'noopener');
      if (!aba) {
        const link = document.createElement('a');
        link.href = url;
        const prefixo = fechamento.status === 'aberto' ? 'previa-malote' : 'fatura-agrupada';
        link.download = `${prefixo}-${agrupamento.nome}-${mes}.pdf`.replace(/[\s/]+/g, '-');
        link.click();
        setAviso('O navegador bloqueou a nova aba, então o arquivo foi baixado.');
      }
      // Solta o blob depois de o navegador abrir: revogar na hora deixa a aba em branco.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível gerar a fatura agrupada.');
    } finally {
      setGerandoPdf(false);
    }
  };

  /**
   * Fatura agrupada em Excel: a relação das UCs do mês com os mesmos números do PDF, em qualquer
   * status do mês (aberto sai como prévia). Só lê: não fecha o mês nem grava nada.
   */
  const baixarExcel = async () => {
    if (!fechamento || !regua) return;
    setGerandoExcel(true);
    setErro(null);
    try {
      // Carregado só no clique: a biblioteca de Excel é pesada.
      const { exportarFaturaAgrupada } = await import('./exportarFaturaAgrupada');
      await exportarFaturaAgrupada(fechamento.id, regua.vencimento, profile?.full_name || user?.email || 'Usuário');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível gerar a planilha.');
    } finally {
      setGerandoExcel(false);
    }
  };

  /**
   * Registra quanto a Prefeitura pagou (#6003). Dentro da faixa esperada (do liquido ao cheio) o
   * mes fecha e as faturas das UCs viram "Quitado"; abaixo do liquido o banco devolve pendencia e
   * a diferenca fica a mostra, em vez de sumir numa baixa automatica.
   */
  const registrarPagamento = async () => {
    if (!fechamento) return;
    setErroPagamento(null);
    const recebido = Number(valorRecebido.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(recebido) || recebido <= 0) { setErroPagamento('Informe o valor recebido.'); return; }
    if (arquivoRetencao) {
      const recusa = motivoArquivoRecusado(arquivoRetencao);
      if (recusa) { setErroPagamento(recusa); return; }
    }
    setSalvandoPagamento(true);
    try {
      const caminho = arquivoRetencao
        ? await enviarComprovanteRetencao(agrupamento.id, fechamento.id, arquivoRetencao)
        : null;
      const { data, error } = await supabase.rpc('registrar_pagamento_fechamento', {
        p_fechamento_id: fechamento.id,
        p_valor_recebido: recebido,
        p_data_pagamento: dataPagamento || null,
        p_observacao: obsPagamento.trim() || null,
        p_comprovante_path: caminho,
      });
      if (error) throw error;
      if ((data as any)?.ok !== true) throw new Error('O banco nao confirmou o registro do pagamento.');
      const situacao = (data as any).situacao as SituacaoPagamento;
      const retido = Number((data as any).valor_retido ?? 0);
      setAviso(situacao === 'pago_a_menor'
        ? `Registrado, mas o valor esta abaixo do esperado: o mes fica pendente. ${ROTULO_SITUACAO_PAGAMENTO[situacao]}.`
        : `Pagamento registrado: ${ROTULO_SITUACAO_PAGAMENTO[situacao]}${retido > 0 ? `, ${reais(retido)} retidos` : ''}.`);
      setRegistrandoPagamento(false);
      setValorRecebido(''); setDataPagamento(''); setObsPagamento(''); setArquivoRetencao(null);
      await carregar();
    } catch (e: any) {
      setErroPagamento(e?.message || 'Nao foi possivel registrar o pagamento.');
    } finally {
      setSalvandoPagamento(false);
    }
  };

  /**
   * Registra a entrega do malote (#5788). O prazo de pagamento NÃO muda com isto: ele conta do
   * limite da régua (5º dia útil), porque é o limite que o contrato fixa. Aqui fica a prova de
   * quem entregou, quando, e o comprovante.
   */
  const registrarMalote = async () => {
    if (!fechamento) return;
    setErroMalote(null);
    if (!dataMalote) { setErroMalote('Informe a data da entrega.'); return; }
    if (arquivoMalote) {
      const recusa = motivoArquivoRecusado(arquivoMalote);
      if (recusa) { setErroMalote(recusa); return; }
    }
    setSalvandoMalote(true);
    try {
      // O arquivo sobe antes: se a gravação falhar, sobra um arquivo órfão no bucket (e o caminho
      // tem o id do fechamento, então é achável) — pior seria gravar a entrega sem o comprovante.
      const caminho = arquivoMalote
        ? await enviarComprovanteMalote(agrupamento.id, fechamento.id, arquivoMalote)
        : null;
      const { data, error } = await supabase.rpc('registrar_malote_fechamento', {
        p_fechamento_id: fechamento.id,
        p_entregue_em: dataMalote,
        p_observacao: obsMalote.trim() || null,
        p_anexo_path: caminho,
      });
      if (error) throw error;
      if ((data as any)?.ok !== true) throw new Error('O banco não confirmou o registro do malote.');
      setRegistrandoMalote(false);
      setArquivoMalote(null);
      setObsMalote('');
      setAviso('Entrega do malote registrada.');
      await carregar();
    } catch (e: any) {
      setErroMalote(e?.message || 'Não foi possível registrar a entrega.');
    } finally {
      setSalvandoMalote(false);
    }
  };

  const desfazerMalote = async () => {
    if (!fechamento) return;
    setConfirmandoDesfazerMalote(false);
    setSalvandoMalote(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('desfazer_malote_fechamento', { p_fechamento_id: fechamento.id });
      if (error) throw error;
      await apagarComprovanteMalote((data as any)?.anexo_path);
      setAviso('Registro do malote desfeito.');
      await carregar();
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível desfazer o registro.');
    } finally {
      setSalvandoMalote(false);
    }
  };

  const abrirComprovante = async () => {
    if (!fechamento?.malote_anexo_path) return;
    try {
      window.open(await linkComprovanteMalote(fechamento.malote_anexo_path), '_blank', 'noopener');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível abrir o comprovante.');
    }
  };

  const abrirComprovanteRetencao = async () => {
    if (!fechamento?.comprovante_retencao_path) return;
    try {
      window.open(await linkComprovanteMalote(fechamento.comprovante_retencao_path), '_blank', 'noopener');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível abrir o comprovante de retenção.');
    }
  };

  const copiarLinha = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      setAviso('Linha digitável copiada.');
    } catch {
      setErro('Não foi possível copiar. Selecione e copie a linha à mão.');
    }
  };

  const reabrirMes = async () => {
    if (!competencia || !motivoTexto.trim()) return;
    const ok = await chamarRpc('reabrir_fechamento_agrupamento',
      { p_agrupamento_id: agrupamento.id, p_competencia: competencia, p_motivo: motivoTexto.trim() },
      r => (r.ja_estava_aberto ? 'Este mês já estava aberto.' : 'Mês reaberto. As faturas voltaram a poder ser alteradas.'));
    // Só fecha o modal e descarta o motivo digitado quando o banco confirma — em caso de erro
    // (ex.: analista sem permissão) o operador continua vendo o motivo e a mensagem real.
    if (ok) { setReabrindo(false); setMotivoTexto(''); }
  };

  const dispensarUc = async () => {
    if (!competencia || !dispensando || !motivoTexto.trim()) return;
    const ok = await chamarRpc('dispensar_uc_do_fechamento',
      { p_agrupamento_id: agrupamento.id, p_competencia: competencia, p_uc_id: dispensando.unidadeConsumidoraId, p_motivo: motivoTexto.trim() },
      () => `UC ${dispensando.numeroUc} dispensada deste mês.`);
    if (ok) { setDispensando(null); setMotivoTexto(''); }
  };

  const removerDispensa = (l: LinhaFechamento) => {
    if (!competencia) return;
    chamarRpc('remover_dispensa_do_fechamento',
      { p_agrupamento_id: agrupamento.id, p_competencia: competencia, p_uc_id: l.unidadeConsumidoraId },
      r => (r.removidas ? `Dispensa da UC ${l.numeroUc} desfeita.` : 'Esta UC não estava dispensada.'));
  };

  const salvarAjusteDatas = (limpar: boolean) => {
    if (!competencia || (!limpar && !ajusteDatas)) return;
    chamarRpc('ajustar_datas_fechamento', {
      p_agrupamento_id: agrupamento.id,
      p_competencia: competencia,
      p_limite_emissao: limpar ? null : (ajusteDatas!.limiteEmissao || null),
      p_auditoria: limpar ? null : (ajusteDatas!.auditoria || null),
      p_vencimento: limpar ? null : (ajusteDatas!.vencimento || null),
      p_malote: limpar ? null : (ajusteDatas!.malote || null),
      p_motivo: limpar ? null : ajusteDatas!.motivo.trim(),
      p_limpar: limpar,
    }, r => (r.limpou ? 'Datas de volta ao padrão da régua.' : 'Datas deste mês ajustadas.'))
      .then(ok => {
        // Só fecha o modal quando o banco confirma — em caso de erro o operador continua
        // vendo o motivo real dentro do próprio modal (o banner do topo fica atrás do overlay).
        if (ok) { setAjusteDatas(null); setConfirmandoPadrao(false); }
      });
  };

  const buscarFaturaCompleta = async (faturaId: number): Promise<Fatura> => {
    const { data, error } = await supabase.from('faturas').select('*').eq('id', faturaId).maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('A fatura não foi encontrada. Atualize a tela e tente de novo.');
    return data as Fatura;
  };

  const liberarTrava = () => {
    const fluxo = fluxoRef.current;
    fluxoRef.current = null;
    if (!fluxo) return;
    supabase.rpc('release_fatura_lock_v2', { p_fatura_id: fluxo.faturaId, p_user_id: fluxo.userId })
      .then(({ error }) => { if (error) console.error('[FaturaLock] liberar (agrupamento):', error); });
  };

  /**
   * Abre a fatura da UC na etapa em que ela está, como o card de Faturas e Cobranças: com a mesma
   * trava de edição (duas pessoas na mesma fatura sobrescrevem uma à outra) e a linha completa.
   */
  const abrirFatura = async (l: LinhaFechamento) => {
    const faturaId = l.conferencia.faturaId;
    if (faturaId == null || abrindoFaturaId != null || fluxoRef.current) return;
    if (!user?.id) { addToast('Usuário não autenticado.', 'error'); return; }
    setAbrindoFaturaId(faturaId);
    try {
      const { data: trava, error: erroTrava } = await supabase.rpc('acquire_fatura_lock_v2', { p_fatura_id: faturaId, p_user_id: user.id });
      if (erroTrava) throw new Error('Não foi possível verificar se a fatura está livre. Tente de novo.');
      const resultado = trava?.[0];
      if (resultado && !resultado.success) {
        addToast(`Esta fatura está sendo editada por ${resultado.locked_by_name || 'outro usuário'}.`, 'warning');
        return;
      }
      fluxoRef.current = { faturaId, userId: user.id };
      const completa = await buscarFaturaCompleta(faturaId);
      if (!etapaDaFatura(completa.status_fatura)) {
        liberarTrava();
        addToast(`A fatura desta UC está em "${completa.status_fatura}": não há ação para essa etapa por aqui.`, 'warning');
        return;
      }
      setFaturaAberta(completa);
    } catch (e: any) {
      liberarTrava();
      addToast(e?.message || 'Não foi possível abrir a fatura.', 'error');
    } finally {
      setAbrindoFaturaId(null);
    }
  };

  const fecharFatura = () => {
    liberarTrava();
    setFaturaAberta(null);
    setTrocandoEtapa(false);
  };

  /**
   * Depois de cada passo do fluxo: atualiza a lista e relê a fatura aberta — é a etapa nova dela
   * que decide o próximo modal. Os modais chamam isto e, em seguida, `onClose` ou `onNext`.
   */
  const atualizarAposFluxo = () => {
    carregar();
    const fluxo = fluxoRef.current;
    if (!fluxo) return;
    buscarFaturaCompleta(fluxo.faturaId)
      .then(f => {
        // O modal pode ter fechado enquanto a leitura voltava: aí não reabre nada.
        if (fluxoRef.current?.faturaId !== fluxo.faturaId) return;
        if (!etapaDaFatura(f.status_fatura)) { fecharFatura(); return; }
        setFaturaAberta(f);
        setTrocandoEtapa(false);
      })
      .catch(e => {
        console.error('Acompanhamento mensal: releitura da fatura do fluxo:', e);
        if (fluxoRef.current?.faturaId !== fluxo.faturaId) return;
        fecharFatura();
        addToast('Não foi possível reler a fatura depois da alteração. Abra de novo para continuar.', 'warning');
      });
  };

  // A trava expira sozinha em 15 min sem batida: mantém viva enquanto o fluxo está aberto.
  const faturaAbertaId = faturaAberta ? Number(faturaAberta.id) : null;
  useEffect(() => {
    const userId = user?.id;
    if (!faturaAbertaId || !userId) return;
    const batida = setInterval(() => {
      supabase.rpc('heartbeat_fatura_lock_v2', { p_fatura_id: faturaAbertaId, p_user_id: userId })
        .then(({ error }) => { if (error) console.error('[FaturaLock] batida (agrupamento):', error); });
    }, 90_000);
    return () => clearInterval(batida);
  }, [faturaAbertaId, user?.id]);

  // Saiu da tela com uma fatura aberta: solta a trava em vez de esperar ela expirar.
  useEffect(() => () => liberarTrava(), []);

  const etapaAberta = faturaAberta && !trocandoEtapa ? etapaDaFatura(faturaAberta.status_fatura) : null;
  /** Status de cada fatura da lista, para o botão dizer o que o clique vai abrir. */
  const statusPorFatura = useMemo(
    () => new Map<number, string | null>(faturas.map(f => [f.id as number, f.status_fatura ?? null])),
    [faturas],
  );

  const confirmarPular = async () => {
    if (!pulando) return;
    const faturaId = pulando.conferencia.faturaId;
    if (!faturaId) { setErroPular('Esta UC não tem fatura no mês: não há o que pular. Espere a fatura chegar.'); return; }
    const camposPulo = estadoPulo.campos;
    if (!camposPulo) { setErroPular(estadoPulo.erro || 'Selecione o motivo.'); return; }
    setGravando(true);
    setErroPular(null);
    try {
      const nome = (user?.user_metadata as any)?.full_name || user?.email || 'Usuário';
      const { error } = await supabase.from('faturas').update({
        status_fatura: 'Com boleto emitido / A enviar',
        pular_calculo_por_nome: nome,
        pular_calculo_em: new Date().toISOString(),
        ...camposPulo,
        ...CAMPOS_ZERADOS_SEM_INJECAO,
      }).eq('id', faturaId);
      if (error) throw error;
      // Só fecha e avisa sucesso depois da gravação confirmada acima — nunca antes.
      setAviso(`UC ${pulando.numeroUc}: cálculo pulado.`);
      setPulando(null); setEstadoPulo(ESTADO_PULO_INICIAL);
      await carregar();
    } catch (e: any) {
      setErroPular(e?.message || 'Não foi possível pular o cálculo.');
    } finally {
      setGravando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="fechamento-mes">Mês de referência</label>
        <select id="fechamento-mes" value={mes} onChange={e => setMes(e.target.value)} className={ESTILO_CAMPO}>
          {opcoesMes.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <button onClick={carregar} disabled={carregando} aria-busy={carregando}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
          <RefreshCw size={15} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
        {fechamento && (
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${estaFechado ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200'}`}>
            Fechamento {fechamento.status}
          </span>
        )}

        {/* Emissão/fechamento é da Secretaria inteira, nunca de uma UC (regra do pedido). */}
        {fechamento?.status === 'aberto' && (
          <button type="button" onClick={() => setConfirmandoFechar(true)}
            disabled={!!trava || acaoFechamento || carregando} aria-busy={acaoFechamento}
            title={trava ?? undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
            <Lock size={15} /> Fechar o mês
          </button>
        )}
        {/* Prévia do malote (#6262): o mesmo documento, antes de fechar. Fica liberada mesmo com UC
            pendente — ver o que falta é metade do motivo de gerar a prévia. */}
        {fechamento?.status === 'aberto' && (
          <button type="button" onClick={baixarFaturaAgrupada} disabled={gerandoPdf || carregando || !regua || linhas.length === 0} aria-busy={gerandoPdf}
            title={linhas.length === 0
              ? 'Este agrupamento ainda não tem UCs'
              : 'Documento de teste com a situação de agora: capa, relação das UCs e demonstrativos. Não fecha o mês nem emite boleto.'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            {gerandoPdf ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Prévia do malote (PDF)
          </button>
        )}
        {(fechamento?.status === 'fechado' || (fechamento?.status === 'emitido' && !fechamento.fatura_boleto_id)) && podeReabrir && (
          <button type="button" onClick={() => { setMotivoTexto(''); setErroAcaoFechamento(null); setReabrindo(true); }} disabled={acaoFechamento}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            <Unlock size={15} /> Reabrir o mês
          </button>
        )}
        {fechamento?.status === 'fechado' && (
          <button type="button" onClick={() => setConfirmandoEmitir(true)}
            disabled={!!bloqueioEmissao || emitindo || acaoFechamento || carregando} aria-busy={emitindo}
            title={bloqueioEmissao ?? undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-sm font-medium text-white hover:bg-secondary/90 disabled:cursor-not-allowed disabled:opacity-60">
            {emitindo ? <Loader2 size={15} className="animate-spin" /> : <Receipt size={15} />} Emitir boleto
          </button>
        )}
        {estaFechado && (
          <button type="button" onClick={baixarFaturaAgrupada} disabled={gerandoPdf || carregando} aria-busy={gerandoPdf}
            title="Capa da cobrança, relação das UCs e o demonstrativo de cada unidade"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            {gerandoPdf ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Fatura agrupada (PDF)
          </button>
        )}
        {fechamento && (
          <button type="button" onClick={baixarExcel} disabled={gerandoExcel || carregando || !regua || linhas.length === 0} aria-busy={gerandoExcel}
            title={linhas.length === 0
              ? 'Este agrupamento ainda não tem UCs'
              : 'Relação das UCs do mês em planilha, com os mesmos valores da fatura agrupada'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
            {gerandoExcel ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Exportar Excel
          </button>
        )}
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{filtradas.length} de {linhas.length} UC(s)</span>
      </div>

      {regua && (
        <section aria-label="Prazos do contrato" aria-live="polite"
          className={`rounded-2xl border p-4 ${ESTILO_REGUA[regua.estado]}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="flex items-center text-xs font-semibold uppercase tracking-wide">
                Régua do contrato
                <InfoTip label="Como o prazo é contado">
                  <p className="mb-2">
                    Emitir até o <strong>último dia útil</strong> do mês seguinte à competência, auditar no
                    <strong> 1º dia útil</strong> e entregar o malote com a NF até o <strong>5º dia útil</strong>.
                    Feriados nacionais, o estadual de Goiás e o municipal de Morrinhos entram na conta.
                  </p>
                  <p>
                    O prazo individual da Janela de Faturamento (vencimento da distribuidora menos 7 dias) não vale
                    para UC de agrupamento ativo: ela pediria justificativa em quase toda fatura da Prefeitura,
                    porque a maioria vence no fim do mês.
                  </p>
                </InfoTip>
              </p>
              <p className="mt-1 text-sm font-bold">{ROTULO_PRAZO[regua.estado]}</p>
              <p className="text-xs">{frasePrazo(regua)}</p>
              {datasSalvas.motivo && (
                <p className="mt-1 text-xs opacity-80">Datas ajustadas: {datasSalvas.motivo}</p>
              )}
              {fechamento?.status === 'aberto' && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" disabled={acaoFechamento}
                    onClick={() => setAjusteDatas({
                      limiteEmissao: datasSalvas.limiteEmissao ?? '',
                      auditoria: datasSalvas.auditoria ?? '',
                      vencimento: datasSalvas.vencimento ?? '',
                      malote: datasSalvas.malote ?? '',
                      motivo: datasSalvas.motivo ?? '',
                    })}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-current/30 px-2.5 py-1 text-xs font-medium hover:bg-black/5 disabled:opacity-60 dark:hover:bg-white/10">
                    <Pencil size={13} /> Ajustar datas
                  </button>
                  {(datasSalvas.limiteEmissao || datasSalvas.auditoria || datasSalvas.vencimento || datasSalvas.malote) && (
                    <button type="button" disabled={acaoFechamento} onClick={() => { setErroAcaoFechamento(null); setConfirmandoPadrao(true); }}
                      className="rounded-lg border border-current/30 px-2.5 py-1 text-xs font-medium hover:bg-black/5 disabled:opacity-60 dark:hover:bg-white/10">
                      Voltar ao padrão
                    </button>
                  )}
                </div>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
              {([
                ['Emitir até', regua.limiteEmissao],
                ['Auditoria', regua.auditoria],
                ['Vencimento', regua.vencimento],
                ['Malote e NF', regua.malote],
              ] as [string, string][]).map(([rotulo, data]) => (
                <div key={rotulo}>
                  <dt className="font-semibold uppercase tracking-wide opacity-70">{rotulo}</dt>
                  <dd className="tabular-nums">
                    {dataBr(data)}
                    {ajustadaPorRotulo[rotulo] && (
                      <span className="ml-1 rounded-full bg-black/10 px-1.5 py-0.5 text-[10px] font-semibold dark:bg-white/15">ajustada</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="mt-2 text-[11px] opacity-80">
            Pagamento pelo edital: até {dataBr(regua.limitePagamentoEdital)} (30 dias após o malote entregue).
          </p>
        </section>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
          <p className="flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Resolvidas
            <InfoTip label="O que conta como resolvida">
              <p className="mb-2">Resolvida = faturada (boleto emitido e registrado), com cálculo pulado com motivo, ou dispensada do mês.</p>
              <p className="mb-2">UC cuja fatura ainda não chegou <strong>não</strong> conta: é preciso esperar, pular ou dispensar com motivo.</p>
              <p>Ao fechar, o banco reconfere tudo: a trava não é só da tela.</p>
            </InfoTip>
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
            {resumo.resolvidas}<span className="text-base font-medium text-gray-400"> de {resumo.ucs}</span>
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Total do boleto</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{reais(resumo.totalVigor)}</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {resumo.faturadas} faturada(s) · {resumo.puladas} pulada(s){resumo.dispensadas ? ` · ${resumo.dispensadas} dispensada(s)` : ''}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Fatura não chegou</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-amber-700 dark:text-amber-400">{resumo.aguardandoFatura}</p>
        </div>
        <button type="button" onClick={() => setSoPendentes(v => !v)} aria-pressed={soPendentes}
          className={`rounded-2xl border p-4 text-left transition-colors ${soPendentes ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-primary dark:border-gray-700'} bg-white dark:bg-gray-800/60`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Falta resolver</p>
          <p className={`mt-1 text-2xl font-bold tabular-nums ${resumo.pendentes ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{resumo.pendentes}</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{soPendentes ? 'Mostrando só pendentes' : 'Clique para filtrar'}</p>
        </button>
      </div>

      {/* Boleto cancelado depois do malote entregue: o que está na Prefeitura é o documento antigo. */}
      {fechamento?.status === 'fechado' && fechamento.malote_entregue_em && (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200" role="status">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>
            <strong>O malote deste mês foi entregue em {dataBr(fechamento.malote_entregue_em)}, mas o boleto não está mais emitido.</strong>{' '}
            A Prefeitura está com a fatura e o boleto antigos. Ao emitir de novo, combine a troca do documento e desfaça o registro se o malote for refeito.
          </p>
        </div>
      )}

      {fechamento?.status === 'fechado' && bloqueioEmissao && (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200" role="status">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p><strong>Ainda não dá para emitir o boleto.</strong> {bloqueioEmissao}</p>
        </div>
      )}

      {fechamento?.status === 'emitido' && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200" aria-label="Boleto do agrupamento">
          <div className="flex flex-wrap items-center gap-2">
            <Receipt size={18} />
            <h3 className="font-bold">
              {fechamento.fatura_boleto_id ? `Boleto emitido: ${reais(Number(fechamento.valor_cobrado ?? 0))}` : 'Emitido sem boleto (total R$ 0,00)'}
            </h3>
            {fechamento.quitado_em && (
              <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">Pago em {dataBr(fechamento.quitado_em)}</span>
            )}
            {fechamento.emitido_em && <span className="text-xs opacity-80">em {dataBr(fechamento.emitido_em)}</span>}
          </div>
          {fechamento.fatura_boleto_id ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center">
              <span className="text-xs font-semibold uppercase opacity-70">Nosso número</span>
              <span className="font-mono">{fechamento.num_cobranca_boleto || '—'}</span>
              <span className="text-xs font-semibold uppercase opacity-70">Linha digitável</span>
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate font-mono">{fechamento.linha_digitavel_boleto || '—'}</span>
                {fechamento.linha_digitavel_boleto && (
                  <button type="button" onClick={() => copiarLinha(fechamento.linha_digitavel_boleto!)} aria-label="Copiar linha digitável"
                    className="rounded-md p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"><Copy size={14} /></button>
                )}
              </span>
              {fechamento.url_boleto && (
                <>
                  <span className="text-xs font-semibold uppercase opacity-70">PDF</span>
                  <a href={fechamento.url_boleto} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium underline">
                    Abrir boleto <ExternalLink size={13} />
                  </a>
                </>
              )}
            </div>
          ) : (
            <p className="mt-2">Nenhum título foi registrado no banco. A relação das UCs do mês continua valendo como documento.</p>
          )}
          {fechamento.fatura_boleto_id && !fechamento.quitado_em && (
            <p className="mt-3 text-xs opacity-80">Quando o banco confirmar o pagamento, as faturas das UCs deste mês viram "Quitado" sozinhas. Se o boleto for cancelado, o mês volta a ficar pronto para emitir de novo.</p>
          )}

          {/* Pagamento com retenção (#6003): o título é cheio e a Prefeitura retém o imposto ao pagar,
              então receber menos que o boleto é esperado — o que não pode é a diferença desaparecer. */}
          {fechamento.fatura_boleto_id && (
            <div className="mt-4 border-t border-emerald-200 pt-3 dark:border-emerald-900">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-bold">Pagamento</h4>
                {fechamento.situacao_pagamento && (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    fechamento.situacao_pagamento === 'pago_a_menor'
                      ? 'bg-amber-500 text-white'
                      : fechamento.situacao_pagamento === 'aguardando'
                        ? 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100'
                        : 'bg-emerald-600 text-white'}`}>
                    {ROTULO_SITUACAO_PAGAMENTO[fechamento.situacao_pagamento]}
                  </span>
                )}
              </div>
              <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
                <dt className="text-xs font-semibold uppercase opacity-70">Valor do boleto</dt>
                <dd>{reais(Number(fechamento.valor_cobrado ?? 0))}</dd>
                {Number(fechamento.valor_abatimento ?? 0) > 0 && (
                  <>
                    <dt className="text-xs font-semibold uppercase opacity-70">Abatimento (retenção)</dt>
                    <dd>
                      {reais(Number(fechamento.valor_abatimento))}
                      <span className="ml-2 text-xs opacity-80">
                        a pagar {reais(Number(fechamento.valor_cobrado ?? 0) - Number(fechamento.valor_abatimento))}
                      </span>
                    </dd>
                  </>
                )}
                {fechamento.valor_recebido !== null && (
                  <>
                    <dt className="text-xs font-semibold uppercase opacity-70">Recebido</dt>
                    <dd>
                      {reais(Number(fechamento.valor_recebido))}
                      {Number(fechamento.valor_retido ?? 0) > 0 && (
                        <span className="ml-2 text-xs opacity-80">{reais(Number(fechamento.valor_retido))} retidos</span>
                      )}
                    </dd>
                  </>
                )}
                {fechamento.pagamento_observacao && (
                  <>
                    <dt className="text-xs font-semibold uppercase opacity-70">Observação</dt>
                    <dd>{fechamento.pagamento_observacao}</dd>
                  </>
                )}
              </dl>
              {fechamento.comprovante_retencao_path && (
                <button type="button" onClick={() => abrirComprovanteRetencao()}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium underline">
                  Abrir comprovante de retenção <ExternalLink size={13} />
                </button>
              )}

              {!registrandoPagamento && (
                <button type="button" onClick={() => { setRegistrandoPagamento(true); setErroPagamento(null); }}
                  className="mt-3 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-transparent dark:text-emerald-100 dark:hover:bg-emerald-900/40">
                  {fechamento.valor_recebido === null ? 'Registrar pagamento' : 'Corrigir pagamento'}
                </button>
              )}

              {registrandoPagamento && (
                <div className="mt-3 grid gap-2 rounded-xl border border-emerald-300 bg-white p-3 dark:border-emerald-800 dark:bg-gray-900">
                  <label className="text-xs font-semibold uppercase opacity-70" htmlFor="pg-valor">Valor recebido</label>
                  <input id="pg-valor" inputMode="decimal" value={valorRecebido} onChange={e => setValorRecebido(e.target.value)}
                    placeholder={String(Number(fechamento.valor_cobrado ?? 0) - Number(fechamento.valor_abatimento ?? 0))}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800" />
                  <label className="text-xs font-semibold uppercase opacity-70" htmlFor="pg-data">Data do pagamento</label>
                  <input id="pg-data" type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800" />
                  <label className="text-xs font-semibold uppercase opacity-70" htmlFor="pg-obs">Observação (opcional)</label>
                  <input id="pg-obs" value={obsPagamento} onChange={e => setObsPagamento(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800" />
                  <label className="text-xs font-semibold uppercase opacity-70" htmlFor="pg-arq">Comprovante de retenção (opcional)</label>
                  <input id="pg-arq" type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={e => setArquivoRetencao(e.target.files?.[0] ?? null)} className="text-sm" />
                  {previaPagamento && (
                    <p className="text-xs opacity-80">
                      {ROTULO_SITUACAO_PAGAMENTO[previaPagamento.situacao]}
                      {previaPagamento.retido > 0 ? ` · ${reais(previaPagamento.retido)} retidos` : ''}
                      {previaPagamento.aviso ? ` · ${previaPagamento.aviso}` : ''}
                    </p>
                  )}
                  {erroPagamento && <p className="text-sm font-medium text-red-700 dark:text-red-300" role="alert">{erroPagamento}</p>}
                  <div className="flex gap-2">
                    <button type="button" onClick={registrarPagamento} disabled={salvandoPagamento}
                      className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60">
                      {salvandoPagamento ? 'Registrando...' : 'Registrar'}
                    </button>
                    <button type="button" onClick={() => { setRegistrandoPagamento(false); setErroPagamento(null); }}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold dark:border-gray-700">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Malote (#5788): o prazo de pagamento conta do limite da régua, não do registro. */}
          {malote && (
            <div className="mt-4 border-t border-emerald-200 pt-3 dark:border-emerald-900">
              <div className="flex flex-wrap items-center gap-2">
                <Package size={16} />
                <strong>{ROTULO_MALOTE[malote.estado]}</strong>
                <span className={`text-xs ${malote.estado === 'atrasado' ? 'font-semibold text-red-700 dark:text-red-400' : 'opacity-80'}`}>{malote.frase}</span>
                {!fechamento.malote_entregue_em && (
                  <button type="button" onClick={() => { setErroMalote(null); setDataMalote(hojeIso()); setRegistrandoMalote(true); }}
                    className="ml-auto rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90">
                    Registrar entrega
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs opacity-80">
                Prazo de pagamento do edital: até {dataBr(malote.limitePagamento)} — contado do limite de entrega, como manda o contrato.
              </p>
              {fechamento.malote_entregue_em && (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                  {fechamento.malote_observacao && <span className="opacity-80">{fechamento.malote_observacao}</span>}
                  {fechamento.malote_anexo_path && (
                    <button type="button" onClick={abrirComprovante} className="inline-flex items-center gap-1 font-medium underline">
                      Abrir comprovante <ExternalLink size={12} />
                    </button>
                  )}
                  <button type="button" onClick={() => setConfirmandoDesfazerMalote(true)} disabled={salvandoMalote}
                    className="text-red-700 underline disabled:opacity-60 dark:text-red-400">
                    Desfazer registro
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {trava ? (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p><strong>Ainda não dá para fechar o mês.</strong> {trava}</p>
        </div>
      ) : (
        <div className="flex gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <p>
            {estaFechado ? (
              <>
                Mês <strong>{fechamento?.status}</strong>
                {fechamento?.fechado_em ? ` em ${dataBr(fechamento.fechado_em)}` : ''}, com total de{' '}
                {reais(Number(fechamento?.total_vigor ?? 0))}. As faturas deste mês ficam bloqueadas: para mexer,
                reabra o mês.
              </>
            ) : (
              <>
                Todas as {resumo.ucs} UC(s) estão resolvidas. Total de {reais(resumo.totalVigor)}
                {resumo.totalVigor === 0 && ' — fechar com zero é válido: o contrato prevê emitir mesmo sem injeção'}.
                Ao fechar, o banco reconfere e guarda quais faturas entram neste mês.
              </>
            )}
          </p>
        </div>
      )}

      {erro && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
          {erro} <button onClick={carregar} className="font-semibold underline">Tentar de novo</button>
        </div>
      )}
      {aviso && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300" role="status">{aviso}</div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar UC ou cliente"
            aria-label="Buscar UC ou cliente" className={`${ESTILO_CAMPO} w-full pl-9`} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">UC</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Fatura</th>
              <th className="px-3 py-2 text-right">Valor Vigor</th>
              <th className="px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visiveis.map(l => {
              const { Icone, classe } = ESTILO_ESTADO[l.estado];
              const temFatura = l.conferencia.faturaId != null;
              const etapaLinha = temFatura ? etapaDaFatura(statusPorFatura.get(l.conferencia.faturaId!)) : null;
              const abrindoEsta = temFatura && abrindoFaturaId === l.conferencia.faturaId;
              return (
                <tr key={l.unidadeConsumidoraId} className="align-top hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-3 py-2">
                    <p className="font-mono text-xs text-gray-900 dark:text-white">{l.numeroUc}</p>
                    <p className="max-w-[200px] truncate text-xs text-gray-500 dark:text-gray-400" title={l.cliente}>{l.cliente || '—'}</p>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${classe}`}>
                      <Icone size={12} /> {ROTULO_ESTADO[l.estado]}
                    </span>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{ROTULO_DETALHE[l.conferencia.detalhe]}</p>
                    {l.conferencia.motivoRotulo && <p className="text-xs text-gray-400">{l.conferencia.motivoRotulo}</p>}
                    {l.estado === 'dispensada' && dispensas.get(l.unidadeConsumidoraId) && (
                      <p className="max-w-[220px] truncate text-xs text-gray-400" title={dispensas.get(l.unidadeConsumidoraId)}>
                        {dispensas.get(l.unidadeConsumidoraId)}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                    {temFatura ? (
                      <>
                        <p>Chegou {dataBr(l.conferencia.chegadaEm)}</p>
                        <p>Vence {dataBr(l.conferencia.dataVencimento)}</p>
                      </>
                    ) : l.conferencia.chegadaPrevista ? (
                      <p className={l.conferencia.chegadaAtrasada ? 'font-semibold text-red-700 dark:text-red-400' : ''}>
                        Prevista {dataBr(l.conferencia.chegadaPrevista)}{l.conferencia.chegadaAtrasada && ' · atrasada'}
                      </p>
                    ) : <p className="text-gray-400">Sem previsão</p>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums text-gray-900 dark:text-white">
                    {l.valorVigor ? reais(l.valorVigor) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => abrirFatura(l)}
                        disabled={!temFatura || estaFechado || abrindoFaturaId != null || trocandoEtapa} aria-busy={abrindoEsta}
                        title={estaFechado ? 'O mês está fechado: reabra para alterar'
                          : !temFatura ? 'A fatura desta UC ainda não chegou'
                          : etapaLinha ? `Abre na etapa em que a fatura está: ${ROTULO_ETAPA_FLUXO[etapaLinha]}` : undefined}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                        {abrindoEsta && <Loader2 size={12} className="animate-spin" />} Faturar
                      </button>
                      <button type="button" onClick={() => { setPulando(l); setEstadoPulo(ESTADO_PULO_INICIAL); setErroPular(null); }}
                        disabled={!temFatura || l.estado === 'pulada' || estaFechado}
                        title={estaFechado ? 'O mês está fechado: reabra para alterar' : !temFatura ? 'A fatura desta UC ainda não chegou' : l.estado === 'pulada' ? 'O cálculo desta UC já foi pulado' : undefined}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                        Pular cálculo
                      </button>
                      {/* Dispensar só existe para UC sem fatura: é a terceira saída da trava (#5784). */}
                      {l.estado === 'dispensada' ? (
                        <button type="button" onClick={() => removerDispensa(l)} disabled={estaFechado || acaoFechamento}
                          title={estaFechado ? 'O mês está fechado: reabra para alterar' : dispensas.get(l.unidadeConsumidoraId) ?? undefined}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                          Desfazer dispensa
                        </button>
                      ) : !temFatura && (
                        <button type="button" onClick={() => { setDispensando(l); setMotivoTexto(''); setErroAcaoFechamento(null); }}
                          disabled={estaFechado || acaoFechamento}
                          title={estaFechado ? 'O mês está fechado: reabra para alterar' : 'A UC sai deste mês com motivo registrado, sem entrar no boleto'}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                          Dispensar do mês
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {visiveis.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  <CalendarClock size={22} className="mx-auto mb-2 text-gray-300" />
                  {linhas.length === 0
                    ? 'Este agrupamento ainda não tem UCs. Atribua as UCs na aba Cadastro.'
                    : busca || soPendentes ? 'Nenhuma UC com esses filtros.' : 'Nenhuma UC neste mês.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtradas.length > pageSize && (
        <TablePagination total={filtradas.length} pagina={pagina} pageSize={pageSize}
          onPagina={setPagina} onPageSize={n => { setPageSize(n); setPagina(0); }} label="UCs" />
      )}

      {/* Fluxo da fatura: abre o modal da etapa em que ela está (src/lib/etapaFluxoFatura.ts), a
          mesma régua de Faturas e Cobranças. */}
      <FaturaValidationModal
        isOpen={etapaAberta === 'validacao'}
        fatura={faturaAberta}
        onClose={fecharFatura}
        onUpdate={atualizarAposFluxo}
        // Validou: some até a fatura voltar como "Fatura validada", que abre o cálculo.
        onNext={() => setTrocandoEtapa(true)}
      />

      {/* Sem `onNext` de propósito: a UC do agrupamento para em "calculada" — o boleto é um só, do
          agrupamento. Seguir para a emissão mostraria, UC por UC, uma tela que só avisa isso. */}
      <FaturaCalculationModal
        isOpen={etapaAberta === 'calculo'}
        fatura={faturaAberta}
        onClose={fecharFatura}
        onUpdate={atualizarAposFluxo}
      />

      {/* Fatura já calculada: mostra o cálculo e permite retroceder a etapa. A emissão individual
          fica travada pelo próprio modal enquanto a UC estiver num agrupamento ativo (#5785). */}
      <BoletoEmissionModal
        isOpen={etapaAberta === 'emissao'}
        fatura={faturaAberta}
        onClose={fecharFatura}
        onUpdate={atualizarAposFluxo}
      />

      {/* Parcelar, quitar e sincronizar status continuam só em Faturas e Cobranças: os modais
          escondem esses botões quando a tela não passa as ações. */}
      <BoletoEmitidoModal
        isOpen={etapaAberta === 'emitido'}
        fatura={faturaAberta}
        onClose={fecharFatura}
        onUpdate={atualizarAposFluxo}
      />

      <CobrancaEnviadaModal
        isOpen={etapaAberta === 'enviada'}
        fatura={faturaAberta}
        onClose={fecharFatura}
        onUpdate={atualizarAposFluxo}
      />

      <ConfirmModal
        isOpen={!!pulando}
        onClose={() => { if (!gravando) { setPulando(null); setEstadoPulo(ESTADO_PULO_INICIAL); setErroPular(null); } }}
        onConfirm={confirmarPular}
        title={`Pular cálculo da UC ${pulando?.numeroUc ?? ''}`}
        message="A UC entra no fechamento com valor zero — sem boleto de energia neste mês — e o motivo fica registrado na fatura. Escolha um motivo para confirmar."
        confirmLabel={gravando ? 'Gravando...' : 'Pular cálculo'}
        loading={gravando}
        confirmDisabled={!estadoPulo.campos}
        variant="warning"
        largura="largo"
      >
        {pulando && (
          <PularCalculoCampo
            key={String(pulando.conferencia.faturaId ?? pulando.numeroUc)}
            faturaId={pulando.conferencia.faturaId ?? null}
            onChange={setEstadoPulo}
          />
        )}
        {erroPular && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroPular}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirmandoFechar}
        onClose={() => { if (!acaoFechamento) setConfirmandoFechar(false); }}
        onConfirm={() => { setConfirmandoFechar(false); fecharMes(); }}
        title={`Fechar ${mes} — ${agrupamento.nome}`}
        message={`${resumo.ucs} UC(s), total de ${reais(resumo.totalVigor)}. O banco vai reconferir e guardar quais faturas entram neste mês; depois disso elas ficam bloqueadas até alguém reabrir.`}
        confirmLabel="Fechar o mês"
        loading={acaoFechamento}
        variant="warning"
      />

      <ConfirmModal
        isOpen={confirmandoDesfazerMalote}
        onClose={() => { if (!salvandoMalote) setConfirmandoDesfazerMalote(false); }}
        onConfirm={desfazerMalote}
        title="Desfazer o registro do malote"
        message={`A data, a observação e o comprovante${fechamento?.malote_anexo_path ? ' (o arquivo é apagado)' : ''} saem do mês. Use quando o registro foi feito por engano ou o malote precisou ser refeito.`}
        confirmLabel="Desfazer registro"
        loading={salvandoMalote}
        variant="danger"
      />

      <ConfirmModal
        isOpen={registrandoMalote}
        onClose={() => { if (!salvandoMalote) setRegistrandoMalote(false); }}
        onConfirm={registrarMalote}
        title={`Registrar entrega do malote — ${mes}`}
        message={`Malote de ${agrupamento.nome}: fatura, relação das UCs e demonstrativo entregues na Prefeitura.${malote ? ` O limite era ${dataBr(malote.limite)}.` : ''}`}
        confirmLabel="Registrar entrega"
        loading={salvandoMalote}
        variant="warning"
      >
        <div className="mt-3 space-y-3 text-left">
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-gray-400" htmlFor="malote-data">Data da entrega</label>
            <input id="malote-data" type="date" value={dataMalote} max={hojeIso()} onChange={e => setDataMalote(e.target.value)} className={ESTILO_CAMPO} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-gray-400" htmlFor="malote-obs">Observação (opcional)</label>
            <input id="malote-obs" value={obsMalote} onChange={e => setObsMalote(e.target.value)} placeholder="Ex.: protocolado no setor de compras"
              className={ESTILO_CAMPO} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold uppercase text-gray-400" htmlFor="malote-arquivo">Comprovante (opcional)</label>
            <input id="malote-arquivo" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={e => setArquivoMalote(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm dark:text-gray-300 dark:file:bg-gray-700" />
            <p className="mt-1 text-[11px] text-gray-400">PDF ou imagem, até 10 MB. Fica guardado em pasta fechada e abre por link temporário.</p>
          </div>
          {erroMalote && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
              {erroMalote}
            </p>
          )}
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirmandoEmitir}
        onClose={() => { if (!emitindo) setConfirmandoEmitir(false); }}
        onConfirm={() => { setConfirmandoEmitir(false); emitirBoleto(); }}
        title={`Emitir boleto de ${mes} — ${agrupamento.nome}`}
        message={valoresBoleto.cobrado > 0
          ? `Um boleto de ${reais(valoresBoleto.cobrado)}${valoresBoleto.abatimento > 0 ? `, com ${reais(valoresBoleto.abatimento)} de abatimento pela retenção — a Prefeitura paga ${reais(valoresBoleto.aPagar)}` : ''}, vencimento ${regua ? dataBr(regua.vencimento) : '—'}, em nome de ${agrupamento.pagador_nome || agrupamento.nome}, na conta ${isContaSicoob(agrupamento.conta_sicoob) ? NOME_CONTA_SICOOB[agrupamento.conta_sicoob] : 'não definida'}. Ele cobra as ${resumo.faturadas} UC(s) calculadas; nenhuma UC recebe boleto próprio.`
          : 'O total do mês é R$ 0,00: nenhum boleto será registrado no banco. O mês fica marcado como emitido e a relação das UCs sai mesmo assim.'}
        confirmLabel={valoresBoleto.cobrado > 0 ? (emitirTeste ? 'Emitir boleto de teste' : 'Emitir boleto') : 'Marcar como emitido'}
        loading={emitindo}
        variant="warning"
      >
        {profile?.role === 'adm' && valoresBoleto.cobrado > 0 && (
          <label className="mt-3 flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={emitirTeste} onChange={e => setEmitirTeste(e.target.checked)} />
            Emitir em teste (boleto fictício, não vai para o banco)
          </label>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={reabrindo}
        onClose={() => { if (!acaoFechamento) { setReabrindo(false); setMotivoTexto(''); setErroAcaoFechamento(null); } }}
        onConfirm={reabrirMes}
        title={`Reabrir ${mes} — ${agrupamento.nome}`}
        message={fechamento?.status === 'emitido'
          ? 'Este mês foi emitido sem boleto no banco (total R$ 0,00), então não há cobrança para cancelar: reabrir desfaz a emissão e as faturas voltam a poder ser alteradas. Só adm e gerente conseguem reabrir, e o motivo fica registrado.'
          : 'As faturas voltam a poder ser alteradas e o total gravado é descartado. Só adm e gerente conseguem reabrir, e o motivo fica registrado.'}
        confirmLabel={acaoFechamento ? 'Reabrindo...' : 'Reabrir o mês'}
        loading={acaoFechamento}
        confirmDisabled={!motivoTexto.trim()}
        variant="danger"
      >
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="motivo-reabrir">
          Motivo <span className="text-red-500">*</span>
        </label>
        <textarea id="motivo-reabrir" value={motivoTexto} onChange={e => setMotivoTexto(e.target.value)}
          placeholder="Por que este mês precisa ser reaberto?"
          className="min-h-[80px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
        {erroAcaoFechamento && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroAcaoFechamento}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!dispensando}
        onClose={() => { if (!acaoFechamento) { setDispensando(null); setMotivoTexto(''); setErroAcaoFechamento(null); } }}
        onConfirm={dispensarUc}
        title={`Dispensar a UC ${dispensando?.numeroUc ?? ''} de ${mes}`}
        message="A UC sai deste mês sem entrar no boleto e passa a contar como resolvida. Vale só enquanto a fatura não chega: se ela chegar, a UC volta a pedir decisão."
        confirmLabel={acaoFechamento ? 'Gravando...' : 'Dispensar'}
        loading={acaoFechamento}
        confirmDisabled={!motivoTexto.trim()}
        variant="warning"
      >
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="motivo-dispensa">
          Motivo <span className="text-red-500">*</span>
        </label>
        <textarea id="motivo-dispensa" value={motivoTexto} onChange={e => setMotivoTexto(e.target.value)}
          placeholder="Ex.: UC sem fatura na Equatorial neste ciclo"
          className="min-h-[80px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
        {erroAcaoFechamento && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroAcaoFechamento}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirmandoPadrao}
        onClose={() => { if (!acaoFechamento) { setConfirmandoPadrao(false); setErroAcaoFechamento(null); } }}
        onConfirm={() => salvarAjusteDatas(true)}
        title={`Voltar ao padrão as datas de ${mes}`}
        message="As datas ajustadas manualmente são descartadas e a régua volta a calcular automaticamente emissão, auditoria, vencimento e malote deste mês."
        confirmLabel={acaoFechamento ? 'Gravando...' : 'Voltar ao padrão'}
        loading={acaoFechamento}
        variant="warning"
      >
        {erroAcaoFechamento && (
          <p className="mt-1 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroAcaoFechamento}
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!ajusteDatas}
        onClose={() => { if (!acaoFechamento) setAjusteDatas(null); }}
        onConfirm={() => salvarAjusteDatas(false)}
        title={`Ajustar as datas de ${mes}`}
        message="Deixe em branco o que deve continuar sendo calculado pela régua. A data escrita aqui vale mesmo caindo em fim de semana ou feriado."
        confirmLabel={acaoFechamento ? 'Gravando...' : 'Salvar datas'}
        loading={acaoFechamento}
        confirmDisabled={!ajusteDatas?.motivo.trim()
          || !(ajusteDatas?.limiteEmissao || ajusteDatas?.auditoria || ajusteDatas?.vencimento || ajusteDatas?.malote)}
        variant="warning"
      >
        <div className="grid gap-3 text-left sm:grid-cols-2">
          {([
            ['limiteEmissao', 'Emitir até', regua?.limiteEmissao],
            ['auditoria', 'Auditoria', regua?.auditoria],
            ['vencimento', 'Vencimento', regua?.vencimento],
            ['malote', 'Malote e NF', regua?.malote],
          ] as [keyof NonNullable<typeof ajusteDatas>, string, string | undefined][]).map(([campo, rotulo, calculada]) => (
            <div key={campo}>
              <label className="mb-1 block text-xs font-bold uppercase text-gray-400" htmlFor={`ajuste-${campo}`}>{rotulo}</label>
              <input id={`ajuste-${campo}`} type="date" value={(ajusteDatas?.[campo] as string) ?? ''}
                onChange={e => setAjusteDatas(a => (a ? { ...a, [campo]: e.target.value } : a))}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
              <p className="mt-1 text-[11px] text-gray-400">Calculada: {calculada ? dataBr(calculada) : '—'}</p>
            </div>
          ))}
        </div>
        <label className="mt-3 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="ajuste-motivo">
          Motivo <span className="text-red-500">*</span>
        </label>
        <textarea id="ajuste-motivo" value={ajusteDatas?.motivo ?? ''}
          onChange={e => setAjusteDatas(a => (a ? { ...a, motivo: e.target.value } : a))}
          placeholder="Por que este mês foge do calendário do contrato?"
          className="mt-1 min-h-[80px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
        {erroAcaoFechamento && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {erroAcaoFechamento}
          </p>
        )}
      </ConfirmModal>

      {carregando && linhas.length === 0 && (
        <p className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 size={16} className="animate-spin" /> Carregando o mês...
        </p>
      )}
    </div>
  );
};

export default AcompanhamentoMensalAgrupamento;
